import { observer, useLocalObservable } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../hooks/useStore';
import { TableDataGrid } from '../../../../lib/components/compound/tableDataGrid';
import Util from '../../../../misc/util';
import { buildExportColumns, ExportColumnDef, TableProps } from './columns';
import { ConfirmationDialog } from '../../../../lib/components';

export const ExportsTable: FC<TableProps> = observer((props) => {
  const { t } = useTranslation();
  const { impexStore, companyStore } = useStore();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState('');

  const openDeleteDialog = (id: string) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const exportColumns = buildExportColumns(t, openDeleteDialog);

  type UIStore = {
    isLoading?: boolean;
    exportList: ExportColumnDef[];
    load: () => Promise<void>;
    startLoading: () => Promise<void>;
    finishLoading: () => void;
  };

  const uiStore = useLocalObservable<UIStore>(() => ({
    isLoading: undefined,
    exportList: [],
    async load() {
      if (!companyStore.company || this.isLoading) {
        this.finishLoading();
        return;
      }
      await this.startLoading();
      this.finishLoading();
    },
    startLoading() {
      this.isLoading = true;
      return impexStore.fetchExportList();
    },
    finishLoading() {
      this.exportList = impexStore.exportList.map((e) => ({
        id: e.id,
        name: e.data?.name ?? '---',
        type: e.export_type,
        created_at: new Date(e.created_at),
        time: new Date(e.created_at),
        owner: e.user,
        fileType: (e.data?.name && Util.getFileExtension(e.data?.name)) ?? '?',
        fileName: e.data?.name ?? '',
        fileUrl: e.data?.url ?? '',
        status: e.status,
        errrorCount: e.errors ? Util.getKeys(e.errors).length : 0,
        errorMessages: Array.isArray(e.errors)
          ? (e.errors as string[]).filter((msg) => typeof msg === 'string')
          : []
      }));
      this.isLoading = false;
    }
  }));

  useEffect(() => {
    uiStore.load();
  }, [companyStore.company]);

  return (
    <>
      <ConfirmationDialog
        variant="warning"
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={async () => {
          await impexStore.deleteExport(deleteId);
          await uiStore.load();
        }}
        title={t('export:DELETE_DIALOG_title')}
        contentText={t('export:DELETE_DIALOG_text')}
        confirmButtonText={t('common:DELETE')}
      />
      <TableDataGrid
        rows={uiStore.exportList}
        columns={exportColumns}
        checkboxSelection={false}
        loading={uiStore.isLoading}
        initialState={{ sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] } }}
        {...props}
      />
    </>
  );
});
